import {
  ArrowsRightLeftIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
  PencilSquareIcon,
  TrashIcon,
} from '@heroicons/react/24/solid';
import { useState } from 'react';

import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';

import { ModuleConfigEditor } from './ModuleConfigEditor';
import type {
  ModuleManifest,
  ModuleRef,
  ModuleRefObject,
  PipelineManifest,
  PipelineStage,
} from './types';
import { checkCompatibility } from './types';

interface ModuleRowProps {
  /** Normalized module reference (always object form — editor normalizes on doc load). */
  moduleRef: ModuleRefObject;
  /** Resolved manifest entry for moduleRef.name. Null if unknown. */
  manifestEntry: ModuleManifest | null;
  /** Full manifest, used to power the "swap module" picker. */
  manifest: PipelineManifest;
  /** Stage this module lives in — filters the swap picker by stage match. */
  stage: PipelineStage | string;
  /** Other modules in this stage, so the swap picker can hide them. */
  siblings: string[];
  onSwap: (next: ModuleRef) => void;
  onRemove: () => void;
  /** Current overrides for this module from doc.module_config[name]. */
  configOverrides: Record<string, unknown>;
  /** Write a single knob value for this module; undefined clears the override. */
  onSetConfig: (
    knobName: string,
    value: string | number | boolean | string[] | null | undefined
  ) => void;
}

export function ModuleRow({
  moduleRef,
  manifestEntry,
  manifest,
  stage,
  siblings,
  onSwap,
  onRemove,
  configOverrides,
  onSetConfig,
}: ModuleRowProps) {
  const [expanded, setExpanded] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);
  const overrideCount = Object.keys(configOverrides).length;
  const hasKnobs =
    !!manifestEntry && Object.keys(manifestEntry.config_knobs ?? {}).length > 0;

  // Swap picker mirrors the add picker: only guard:true modules are
  // selectable. The currently-rendered moduleRef may itself be guard:false
  // (if it was loaded from a custom YAML), in which case we still show
  // the row but the swap menu will be empty/limited.
  //
  // Compatibility check is against the OTHER siblings (not including this
  // module — we're replacing it). If the candidate's incompatible_with
  // intersects any other sibling, OR any other sibling excludes the
  // candidate, hide it from the swap menu.
  const siblingManifests: ModuleManifest[] = siblings
    .map(n => manifest.modules[n])
    .filter((m): m is ModuleManifest => Boolean(m));

  const swapCandidates = Object.values(manifest.modules).filter(m => {
    if (m.guard === false) return false;
    const stages = Array.isArray(m.stage) ? m.stage : [m.stage];
    if (!stages.includes(stage as PipelineStage)) return false;
    if (siblings.includes(m.name)) return false;
    if (checkCompatibility(m, siblingManifests) !== null) return false;
    return true;
  });

  // Detect if THIS module is currently violating a compatibility rule
  // (e.g. user loaded a custom YAML with both scored-functions and
  // scored-repo). Surface a warning chip on the row so they see the
  // problem before clicking Run.
  const compatibilityIssue = manifestEntry
    ? checkCompatibility(manifestEntry, siblingManifests)
    : null;

  const handleSwap = (newName: string) => {
    onSwap({ ...moduleRef, name: newName });
  };

  const unknown = !manifestEntry;
  const hasFallback = stage === 'ingest' && moduleRef.fallback;

  const calibrationBlocker = (() => {
    if (!manifestEntry) return null;
    const model = manifestEntry.cost_model;
    if (model.type === 'composite') {
      const blocked = model.components.find(
        c =>
          c.confidence === 0 &&
          c.type !== 'fixed' &&
          (c.calibration?.experiment_id === null || c.calibration?.blocker)
      );
      return blocked?.calibration?.blocker ?? null;
    }
    if (model.type === 'fixed') return null;
    return model.calibration?.blocker ?? null;
  })();

  const descriptionPreview = manifestEntry?.description?.split('. ')[0];

  return (
    <div
      className={cn(
        'border-chariot-stroke group bg-chariot-background-primary relative flex flex-col rounded-md border transition-colors',
        expanded && 'bg-chariot-background-secondary/40'
      )}
    >
      {/* Header — clicking anywhere on it toggles expansion */}
      <button
        type="button"
        onClick={() => setExpanded(prev => !prev)}
        aria-label={
          expanded ? `Collapse ${moduleRef.name}` : `Expand ${moduleRef.name}`
        }
        aria-expanded={expanded}
        className="flex items-start gap-3 px-3 py-2.5 text-left"
      >
        <div className="text-chariot-text-secondary mt-0.5 shrink-0">
          {expanded ? (
            <ChevronDownIcon className="size-3.5" />
          ) : (
            <ChevronRightIcon className="size-3.5" />
          )}
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-1">
          {/* Line 1: name + tiny status icon (full chips moved to line 3) */}
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span
              className={cn(
                'text-sm font-semibold',
                unknown
                  ? 'text-chariot-status-warning'
                  : 'text-chariot-text-primary'
              )}
            >
              {manifestEntry?.display_name ?? moduleRef.name}
            </span>
            {calibrationBlocker && (
              <ExclamationTriangleIcon
                className="text-chariot-status-warning size-3.5 shrink-0"
                aria-label="Cost estimate unavailable"
              />
            )}
          </div>

          {/* Line 2: 1-line description preview — collapsed state only */}
          {!expanded && descriptionPreview && (
            <p className="text-chariot-text-secondary line-clamp-1 text-xs leading-snug">
              {descriptionPreview}.
            </p>
          )}

          {/* Line 3: status chips on their own row so they never crowd the name */}
          {(unknown ||
            hasFallback ||
            calibrationBlocker ||
            compatibilityIssue) && (
            <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
              {unknown && (
                <Badge
                  variant="outline"
                  className="border-chariot-status-warning text-chariot-status-warning gap-1 px-1.5 py-0 text-[10px] font-semibold tracking-wider"
                >
                  <ExclamationTriangleIcon className="size-3" />
                  UNKNOWN
                </Badge>
              )}
              {hasFallback && (
                <Badge
                  variant="secondary"
                  className="bg-chariot-background-secondary text-chariot-text-secondary gap-1 px-1.5 py-0 text-[10px] tracking-wider"
                >
                  fallback → {moduleRef.fallback}
                </Badge>
              )}
              {compatibilityIssue && (
                <Badge
                  variant="outline"
                  className="border-chariot-status-critical text-chariot-status-critical gap-1 px-1.5 py-0 text-[10px] font-semibold tracking-wider"
                  title={
                    compatibilityIssue.kind === 'candidate_excludes_sibling'
                      ? `This module declares it can't run alongside ${compatibilityIssue.siblingName}.`
                      : `${compatibilityIssue.siblingName} declares it can't run alongside this module.`
                  }
                >
                  <ExclamationTriangleIcon className="size-3" />
                  INCOMPATIBLE WITH {compatibilityIssue.siblingName}
                </Badge>
              )}
              {calibrationBlocker && (
                <Badge
                  variant="outline"
                  className="border-chariot-status-warning/60 text-chariot-status-warning gap-1 px-1.5 py-0 text-[10px] font-semibold tracking-wider"
                >
                  EST. UNAVAILABLE
                </Badge>
              )}
            </div>
          )}
        </div>

        {/* Actions — visible on hover or when row is expanded */}
        <div
          className={cn(
            'flex shrink-0 items-center gap-0.5 transition-opacity',
            expanded
              ? 'opacity-100'
              : 'opacity-0 group-hover:opacity-100 focus-within:opacity-100'
          )}
        >
          {hasKnobs && manifestEntry && (
            <span
              role="button"
              tabIndex={0}
              aria-label={`Edit ${moduleRef.name} configuration`}
              onClick={e => {
                e.stopPropagation();
                setConfigOpen(true);
              }}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.stopPropagation();
                  setConfigOpen(true);
                }
              }}
              className="text-chariot-text-secondary hover:text-chariot-text-primary inline-flex h-7 cursor-pointer items-center gap-1 rounded px-2 text-xs"
            >
              <PencilSquareIcon className="size-3.5" />
              Edit
              {overrideCount > 0 && (
                <span
                  className="bg-chariot-status-info/15 text-chariot-status-info ml-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-bold"
                  aria-label={`${overrideCount} overridden`}
                >
                  {overrideCount}
                </span>
              )}
            </span>
          )}
          {swapCandidates.length > 0 && (
            <Popover>
              <PopoverTrigger asChild>
                <span
                  role="button"
                  tabIndex={0}
                  aria-label={`Swap ${moduleRef.name}`}
                  onClick={e => e.stopPropagation()}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.stopPropagation();
                    }
                  }}
                  className="text-chariot-text-secondary hover:text-chariot-text-primary inline-flex h-7 cursor-pointer items-center gap-1 rounded px-2 text-xs"
                >
                  <ArrowsRightLeftIcon className="size-3.5" />
                  Swap
                </span>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-80 p-1">
                <div className="flex flex-col">
                  {swapCandidates.map(candidate => (
                    <button
                      type="button"
                      key={candidate.name}
                      onClick={() => handleSwap(candidate.name)}
                      className="hover:bg-chariot-background-secondary flex flex-col items-start gap-1 rounded-sm px-3 py-2.5 text-left"
                    >
                      <span className="text-chariot-text-primary text-sm font-semibold">
                        {candidate.display_name}
                      </span>
                      <span className="text-chariot-text-secondary line-clamp-2 text-xs">
                        {candidate.description}
                      </span>
                    </button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          )}
          <span
            role="button"
            tabIndex={0}
            aria-label={`Remove ${moduleRef.name}`}
            onClick={e => {
              e.stopPropagation();
              onRemove();
            }}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation();
                onRemove();
              }
            }}
            className="text-chariot-text-secondary hover:text-chariot-status-critical inline-flex size-7 cursor-pointer items-center justify-center rounded"
          >
            <TrashIcon className="size-3.5" />
          </span>
        </div>
      </button>

      {/* Expansion body */}
      {expanded && manifestEntry && (
        <div className="border-chariot-stroke flex flex-col gap-3 border-t px-3 py-3 pl-[2.4rem]">
          <p className="text-chariot-text-primary text-xs leading-relaxed">
            {manifestEntry.description}
          </p>

          {manifestEntry.trade_offs && (
            <div className="border-chariot-stroke border-l-2 pl-3">
              <span className="text-chariot-text-primary text-xs font-semibold">
                Trade-offs.{' '}
              </span>
              <span className="text-chariot-text-secondary text-xs leading-relaxed">
                {manifestEntry.trade_offs}
              </span>
            </div>
          )}

          <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1.5 text-xs">
            {manifestEntry.model_tiers_used.length > 0 && (
              <>
                <dt className="text-chariot-text-secondary">Model tiers</dt>
                <dd className="text-chariot-text-primary">
                  {manifestEntry.model_tiers_used.join(' + ')}
                </dd>
              </>
            )}

            <dt className="text-chariot-text-secondary">Stage</dt>
            <dd className="text-chariot-text-primary">
              {Array.isArray(manifestEntry.stage)
                ? manifestEntry.stage.join(', ')
                : manifestEntry.stage}
            </dd>

            {manifestEntry.best_for.length > 0 && (
              <>
                <dt className="text-chariot-text-secondary">Best for</dt>
                <dd className="flex flex-wrap items-center gap-1.5">
                  {manifestEntry.best_for.map((tag, idx) => {
                    const label =
                      tag.language ?? tag.surface ?? tag.asset_class ?? '?';
                    return (
                      <Badge
                        key={`${label}-${idx}`}
                        variant="secondary"
                        className="px-1.5 py-0 text-[10px]"
                      >
                        {label}
                      </Badge>
                    );
                  })}
                </dd>
              </>
            )}

            {manifestEntry.bench && (
              <>
                <dt className="text-chariot-text-secondary">Bench</dt>
                <dd className="text-chariot-text-primary">
                  <span className="font-mono">
                    {manifestEntry.bench.metric}={manifestEntry.bench.value}
                  </span>{' '}
                  on {manifestEntry.bench.dataset}
                </dd>
              </>
            )}
          </dl>

          {calibrationBlocker && (
            <div className="border-chariot-status-warning/40 bg-chariot-status-warning/5 text-chariot-text-secondary rounded-sm border px-3 py-2 text-xs leading-relaxed">
              <div className="text-chariot-status-warning mb-0.5 flex items-center gap-1.5 font-semibold">
                <ExclamationTriangleIcon className="size-3.5" />
                Cost estimate unavailable
              </div>
              {calibrationBlocker}
            </div>
          )}
        </div>
      )}

      {expanded && unknown && (
        <div className="border-chariot-stroke text-chariot-text-secondary border-t px-3 py-2 text-xs">
          This module isn&apos;t in the manifest. The pipeline will still run if
          the sidecar knows about it, but cost cannot be estimated.
        </div>
      )}

      {hasKnobs && manifestEntry && (
        <ModuleConfigEditor
          open={configOpen}
          onOpenChange={setConfigOpen}
          moduleManifest={manifestEntry}
          overrides={configOverrides}
          onUpdate={onSetConfig}
        />
      )}
    </div>
  );
}
